import { ReportProblemOutlined as ReportProblemOutlinedIcon } from '@mui/icons-material';
import { Box, Link, Typography } from '@mui/material';

import { themeColors } from 'colors';

const alertBarSx = {
    height: 52,
    minHeight: 52,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 1.5,
    borderRadius: 1,
    bgcolor: themeColors.roleBuilder.crossModuleAutoEnableAlertBackground,
    overflow: 'hidden',
} as const;

const messageTextSx = {
    flex: 1,
    minWidth: 0,
    m: 0,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
} as const;

const reportProblemIconSx = {
    color: 'warning.dark',
    flexShrink: 0,
} as const;

const crossModuleAlertLinkSx = {
    color: themeColors.roleBuilder.crossModuleAlertModuleLink,
    verticalAlign: 'baseline',
    cursor: 'pointer',
    p: 0,
    border: 0,
    background: 'none',
    font: 'inherit',
    lineHeight: 'inherit',
    textAlign: 'inherit',
    textDecoration: 'none',
    '&:hover': {
        color: themeColors.roleBuilder.crossModuleAlertModuleLink,
        textDecoration: 'none',
    },
} as const;

export type CrossModuleLinkItem = {
    moduleKey: string;
    label: string;
};

export type CrossModuleAutoEnableAlertProps = {
    prefix: string;
    linkedModules: CrossModuleLinkItem[];
    onModuleClick: (moduleKey: string) => void;
    'data-testid'?: string;
};

/**
 * Yellow info strip for role builder permission modules when enabling permissions here
 * auto-enables dependencies in other modules.
 */
const CrossModuleAutoEnableAlert = ({
    prefix,
    linkedModules,
    onModuleClick,
    'data-testid': dataTestId,
}: CrossModuleAutoEnableAlertProps) => {
    if (linkedModules.length === 0) {
        return null;
    }

    return (
        <Box sx={alertBarSx} data-testid={dataTestId}>
            <ReportProblemOutlinedIcon fontSize="small" sx={reportProblemIconSx} aria-hidden />
            <Typography variant="body2" color="text.primary" component="p" sx={messageTextSx}>
                <Box component="span">{prefix}</Box>
                {linkedModules.map((item, index) => (
                    <Box component="span" key={item.moduleKey}>
                        {index > 0 ? ' or ' : null}
                        <Link
                            component="button"
                            type="button"
                            underline="none"
                            onClick={(e) => {
                                e.preventDefault();
                                onModuleClick(item.moduleKey);
                            }}
                            sx={crossModuleAlertLinkSx}
                        >
                            {item.label}
                        </Link>
                    </Box>
                ))}
                <Box component="span">.</Box>
            </Typography>
        </Box>
    );
};

export default CrossModuleAutoEnableAlert;
